import { computed, reactive, toRefs } from 'vue'
import { Post, SettingsStatus } from '@/model/data-object/Post'
import useConfiguration from '@/composables/configuration/configuration'
import useAppState, { ViewType } from '@/composables/ui/ui'
import useCurrentUser from '@/composables/user/user'
import useTopic from '@/composables/topic/topic'
import { TopicStatus } from '@/model/data-object/TopicSettings'
import usePostsLoader from '@/composables/post/postLoader'

class PostSingleUiActions {
  postEditing = false
}
export default function usePostSingleUiActions(post: Post) {
  const configuration = useConfiguration()
  const { currentUser } = useCurrentUser()

  const isMinePost =
    typeof currentUser.value !== 'undefined' &&
    currentUser.value.id === post.author.id

  const postSingleUiActions = reactive<PostSingleUiActions>(
    new PostSingleUiActions()
  )

  const isHidden = () => {
    return [SettingsStatus.HIDDEN, SettingsStatus.GDPR_DELETED].includes(
      post.status
    )
  }

  const isWriteActionDisabled = () => {
    return configuration.configuration.extSysConf.isWriteDisabled()
  }

  const postActionsDisabled = computed(() => {
    return isHidden() || isWriteActionDisabled()
  })

  const postSuperLikeDisabled = computed(() => {
    return postActionsDisabled.value || isMinePost
  })

  const respondDisabled = computed(() => {
    return isHidden() || isWriteActionDisabled()
  })

  const openRespondPageDisabled = computed(() => {
    return isWriteActionDisabled()
  })

  return {
    isMinePost,
    postActionsDisabled,
    respondDisabled,
    openRespondPageDisabled,
    postSuperLikeDisabled,
    ...toRefs(postSingleUiActions),
  }
}

export function usePostListUiActions() {
  const { viewType } = useAppState()
  const configuration = useConfiguration()
  const {
    allowPayingCustomerAction,
    currentUser,
    isLoggedIn,
    isSubscriber,
  } = useCurrentUser()
  const { topic } = useTopic()
  const { loadedCount } = usePostsLoader()

  const showTopCreateForm = computed(() => {
    return (
      allowPayingCustomerAction.value &&
      viewType.value !== ViewType.TOP_NEWEST &&
      currentUser.value &&
      !currentUser.value.isBanned &&
      !configuration.configuration.extSysConf.isWriteDisabled()
    )
  })

  const showBottomCreateForm = computed(() => {
    return (
      loadedCount.value > 0 &&
      allowPayingCustomerAction.value &&
      viewType.value !== ViewType.TOP_NEWEST &&
      currentUser.value &&
      !currentUser.value.isBanned &&
      !configuration.configuration.extSysConf.isWriteDisabled()
    )
  })

  const showSubscribeTopicButton = computed(() => {
    return (
      isLoggedIn.value &&
      !configuration.configuration.extSysConf.isWriteDisabled()
    )
  })

  const showTopLoginBanner = computed(() => {
    return (
      topic.value &&
      topic.value.settings.status === TopicStatus.ACTIVE &&
      (!isLoggedIn.value || !isSubscriber.value)
    )
  })

  const showTechnicalShutdownBanner = computed(() => {
    return (
      configuration.configuration.extSysConf.isWriteDisabled() ||
      configuration.configuration.extSysConf.isAllDisabled()
    )
  })

  return {
    showTopCreateForm,
    showBottomCreateForm,
    showSubscribeTopicButton,
    showTopLoginBanner,
    showTechnicalShutdownBanner,
  }
}

export function useSubscriberActions() {
  const configuration = useConfiguration()
  const { loggedIn } = useAppState()

  const isWriteActionDisabled = () => {
    return configuration.configuration.extSysConf.isWriteDisabled()
  }

  const isProfileWriteDisabled = computed(() => {
    return !loggedIn.value || isWriteActionDisabled()
  })

  return {
    isProfileWriteDisabled,
  }
}
